import { Bot, ChevronDown, ChevronUp } from "lucide-mui";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { Fragment, type ReactNode, useState } from "react";
import type { AgentRun, AgentRunTranscript as TranscriptPage } from "../../api/client.ts";
import { isJobActive } from "../../utils/status.ts";
import FormattedDate from "../FormattedDate.tsx";
import AgentRunStatusIcon from "./AgentRunStatusIcon.tsx";
import AgentRunTranscript from "./AgentRunTranscript.tsx";
import { useCanSeeTranscripts } from "./useCanSeeTranscripts.ts";

/**
 * Cells per row in the `table` variant: start, summary, turns, tokens, status;
 * one more for the transcript toggle (developers) and one with `actions`. The
 * table head must match.
 */
const AGENT_RUN_TABLE_COLUMNS = 5;

function RunOutput({ output }: { output: Record<string, unknown> }) {
  return (
    <Box
      component="dl"
      sx={{ mt: 1, mb: 0, display: "grid", gridTemplateColumns: "auto 1fr", columnGap: 2, rowGap: 0.5 }}
    >
      {Object.entries(output).map(([key, value]) => (
        <Fragment key={key}>
          <Typography
            component="dt"
            variant="caption"
            sx={{ color: "text.secondary", fontFamily: "monospace" }}
          >
            {key}
          </Typography>
          <Typography component="dd" variant="body2" sx={{ m: 0 }}>
            {value === null || value === undefined ? "—" : String(value)}
          </Typography>
        </Fragment>
      ))}
    </Box>
  );
}

/**
 * One agent run, its status the icon at the end of the row. Developers can
 * unfold its transcript beneath it (always folded at first); the owner of the
 * run (a prospect order, a signal) supplies `fetchTranscript`, which hits its
 * own access-checked transcript endpoint.
 *
 * `table` renders two rows of `AGENT_RUN_TABLE_COLUMNS` cells (the transcript
 * toggle for developers, start, summary, turns, tokens, status, then `actions`
 * when given); `stack` renders a block that names the agent and, with
 * `showReport`, what it reported.
 */
export default function AgentRunRow({
  run,
  fetchTranscript,
  variant = "stack",
  showReport = false,
  actions,
}: {
  run: AgentRun;
  fetchTranscript: (afterSeq: number) => Promise<TranscriptPage>;
  variant?: "stack" | "table";
  showReport?: boolean;
  /** The `table` variant's last cell: the row's `RowActions`. */
  actions?: ReactNode;
}) {
  const canSeeTranscript = useCanSeeTranscripts();
  const [open, setOpen] = useState(false);
  const agent = run.config.agent as { name?: string } | undefined;
  const status = <AgentRunStatusIcon status={run.status} agentName={agent?.name} error={run.error} />;
  const transcript = canSeeTranscript && (
    <Collapse in={open} timeout="auto" unmountOnExit>
      <Box sx={{ pb: variant === "table" ? 2 : 0 }}>
        <AgentRunTranscript runId={run.id} initiallyActive={isJobActive(run.status)} fetchPage={fetchTranscript} />
      </Box>
    </Collapse>
  );

  if (variant === "table") {
    return (
      <>
        <TableRow sx={{ "& > *": { borderBottom: open ? "unset" : undefined } }}>
          {canSeeTranscript && (
            <TableCell>
              <IconButton size="small" onClick={() => setOpen(!open)} aria-label="transcript">
                {open ? <ChevronUp /> : <ChevronDown />}
              </IconButton>
            </TableCell>
          )}
          <TableCell>
            <FormattedDate value={run.started_at} format="datetime" />
          </TableCell>
          <TableCell sx={{ maxWidth: 420 }}>
            <Typography
              variant="body2"
              color={run.error ? "error" : undefined}
              sx={{ whiteSpace: "pre-wrap" }}
            >
              {run.error ?? run.summary ?? "-"}
            </Typography>
          </TableCell>
          <TableCell align="right">{run.iterations_used ?? "-"}</TableCell>
          <TableCell align="right">
            {run.total_input_tokens ?? "-"} / {run.total_output_tokens ?? "-"}
          </TableCell>
          <TableCell padding="checkbox" align="right">
            {status}
          </TableCell>
          {actions !== undefined && (
            <TableCell padding="checkbox" align="right">
              {actions}
            </TableCell>
          )}
        </TableRow>
        {canSeeTranscript && (
          <TableRow>
            <TableCell
              colSpan={AGENT_RUN_TABLE_COLUMNS + 1 + (actions !== undefined ? 1 : 0)}
              sx={{ py: 0, borderBottom: open ? undefined : "unset" }}
            >
              {transcript}
            </TableCell>
          </TableRow>
        )}
      </>
    );
  }

  const trigger = run.config.trigger === "manual" ? "manual" : "automatic";
  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
        <Bot fontSize="small" style={{ opacity: 0.6 }} />
        {agent?.name && (
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {agent.name}
          </Typography>
        )}
        <Typography variant="body2">
          <FormattedDate value={run.created_at} format="datetime" />
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {trigger}
          {run.iterations_used ? ` · ${run.iterations_used} turns` : ""}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        {canSeeTranscript && (
          <Button size="small" onClick={() => setOpen(!open)}>
            {open ? "Hide transcript" : "Transcript"}
          </Button>
        )}
        {status}
      </Stack>
      {run.error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {run.error}
        </Alert>
      )}
      {showReport && run.output && <RunOutput output={run.output} />}
      {showReport && run.summary && (
        <Typography variant="body2" sx={{ mt: 1, whiteSpace: "pre-wrap" }}>
          {run.summary}
        </Typography>
      )}
      {transcript}
    </Box>
  );
}
